import { useState, useEffect } from "react";
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { UploadAvatarInput } from "@/components/common/UploadAvatarInput";
import { getSignedUrl, supabase, uploadAvatar } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface AvatarFieldProps {
  profile: any;
  readOnly: boolean;
  loading: boolean;
}

export function AvatarField({ profile, readOnly, loading }: AvatarFieldProps) {
  const [avatar, setAvatar] = useState<File | null>(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(
    null
  );
  const { toast } = useToast();
  const { t } = useTranslation(["talent", "common"]);

  useEffect(() => {
    if (profile.id && profile.profile_picture) {
      getSignedUrl("profiles", profile.profile_picture).then((url) => {
        setProfilePictureUrl(url);
      });
    }
  }, [profile.id, profile.profile_picture]);

  const handleUploadAvatar = async () => {
    if (!avatar) return;
    try {
      const result = await uploadAvatar(avatar, profile.id);

      if (!result.success) {
        let errorMessage: string;
        switch (result.error) {
          case "INVALID_TYPE":
            errorMessage = t("common:errors.invalid_file_type");
            break;
          case "FILE_TOO_LARGE":
            errorMessage = t("common:errors.file_too_large");
            break;
          case "UPLOAD_FAILED":
          default:
            errorMessage = t("common:errors.upload_failed");
            break;
        }
        toast({
          title: t("common:status.error"),
          description: errorMessage,
          variant: "destructive"
        });
        return;
      }

      const { error } = await supabase
        .from("talent_profiles")
        .update({ profile_picture: result.path })
        .eq("id", profile.id);

      if (error) throw error;

      const url = await getSignedUrl("profiles", result.path);
      setProfilePictureUrl(url);
      toast({
        title: t("common:status.success"),
        description: t("common:messages.profile_picture_uploaded")
      });
    } catch (error) {
      toast({
        title: t("common:status.error"),
        description: t("common:errors.profile_picture_upload_error"),
        variant: "destructive"
      });
    }
  };

  if (readOnly) return null;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <label className="text-sm font-medium">
          {t("forms.sections.personal_data.fields.profile_picture.label")}
        </label>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <HelpCircle className="h-4 w-4 text-gray-400" />
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {t("forms.sections.personal_data.fields.profile_picture.help")}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div className="flex items-center gap-3">
        {profilePictureUrl && (
          <img
            src={profilePictureUrl}
            alt="Profile"
            className="h-10 w-10 rounded-full object-cover border"
          />
        )}
        <UploadAvatarInput
          readOnly={readOnly}
          setAvatar={setAvatar}
          handleUploadAvatar={handleUploadAvatar}
          buttonLabel={t("forms.common.buttons.upload")}
          loading={loading}
        />
      </div>
    </div>
  );
}
